import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import {
  ACTION_SCHEMAS,
  TRIGGER_SCHEMAS,
  createDefaultActionConfig,
  createDefaultTrigger,
  validateAction,
  validateTrigger,
  type RegistryField,
} from '../../../../services/automationRegistry'
import type {
  AutomationAction,
  AutomationActionType,
  AutomationCondition,
  AutomationConditionClause,
  AutomationConditionOp,
  AutomationTrigger,
  AutomationTriggerType,
} from '../../../../types/automation'
import { type GraphDraft } from '../../../Automate/automationGraphDraft'
import { Button } from '../../../Common/Button'
import { NumberStepper } from '../../../Common/NumberStepper'
import {
  type AutomationPayload,
  normalizeAutomationPayload,
  automationPayloadToDraft,
  automationDraftToPayload,
} from './automationPayload'

export type { AutomationPayload }

type AutomationPayloadEditorProps = {
  value: AutomationPayload
  onChange: (next: AutomationPayload) => void
  disabled?: boolean
}

type Step = 'trigger' | 'actions' | 'guards' | 'save'

const STEPS: Array<{ id: Step; label: string }> = [
  { id: 'trigger', label: 'Trigger' },
  { id: 'actions', label: 'Actions' },
  { id: 'guards', label: 'Guards' },
  { id: 'save', label: 'Save' },
]

const CONDITION_OPS: AutomationConditionOp[] = ['EQ', 'NEQ', 'IN', 'NOT_IN', 'GT', 'GTE', 'LT', 'LTE', 'CONTAINS', 'NOT_CONTAINS', 'EXISTS', 'NOT_EXISTS']

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}

const parseJsonObject = (value: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(value)
    return asRecord(parsed)
  } catch {
    return {}
  }
}

const parseConditionValue = (value: string): unknown => {
  if (!value.trim()) return undefined
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const reorder = <T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) return items
  const next = [...items]
  const [moved] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, moved)
  return next
}

const withoutEmptyCondition = (condition: AutomationCondition): AutomationCondition | undefined =>
  condition.clauses.length > 0 ? condition : undefined

export const AutomationPayloadEditor = ({ value, onChange, disabled = false }: AutomationPayloadEditorProps) => {
  const normalizedValue = useMemo(() => normalizeAutomationPayload(value), [value])
  const [draft, setDraft] = useState(() => automationPayloadToDraft(normalizedValue))
  const [activeStep, setActiveStep] = useState<Step>('trigger')
  const [triggerSearch, setTriggerSearch] = useState('')
  const [actionTypeToAdd, setActionTypeToAdd] = useState<AutomationActionType>('NOTIFY_USER')
  const emittedInitialValueRef = useRef(false)
  const draggedTriggerRef = useRef<number | null>(null)
  const draggedActionRef = useRef<number | null>(null)
  const draggedConditionRef = useRef<number | null>(null)

  const compiledPayload = useMemo(() => automationDraftToPayload(draft, normalizedValue), [draft, normalizedValue])
  const validation = useMemo(() => [
    ...draft.triggers.map(validateTrigger),
    ...draft.then.map(validateAction),
  ].filter(Boolean) as string[], [draft.then, draft.triggers])
  const triggersForSearch = useMemo(() => {
    const query = triggerSearch.trim().toLowerCase()
    const all = Object.values(TRIGGER_SCHEMAS)
    if (!query) return all
    return all.filter((schema) => `${schema.type} ${schema.label} ${schema.description}`.toLowerCase().includes(query))
  }, [triggerSearch])
  const conditions = draft.if?.clauses ?? []

  useEffect(() => {
    if (emittedInitialValueRef.current) return
    emittedInitialValueRef.current = true
    onChange(normalizedValue)
  }, [normalizedValue, onChange])

  const emitDraft = (nextDraft: GraphDraft) => {
    setDraft(nextDraft)
    onChange(automationDraftToPayload(nextDraft, normalizedValue))
  }

  const updateTrigger = (index: number, next: AutomationTrigger) => emitDraft({
    ...draft,
    triggers: draft.triggers.map((trigger, itemIndex) => itemIndex === index ? next : trigger),
  })

  const addTrigger = (type: AutomationTriggerType) => {
    if (draft.triggers.length >= 2) return
    emitDraft({ ...draft, triggers: [...draft.triggers, createDefaultTrigger(type)] })
  }

  const updateAction = (index: number, next: AutomationAction) => emitDraft({
    ...draft,
    then: draft.then.map((action, itemIndex) => itemIndex === index ? next : action),
  })

  const addAction = () => emitDraft({
    ...draft,
    then: [...draft.then, { id: `a${draft.then.length + 1}`, type: actionTypeToAdd, config: createDefaultActionConfig(actionTypeToAdd) } as AutomationAction],
  })

  const updateCondition = (index: number, clause: AutomationConditionClause) => emitDraft({
    ...draft,
    if: withoutEmptyCondition({ join: draft.if?.join ?? 'AND', clauses: conditions.map((item, itemIndex) => itemIndex === index ? clause : item) }),
  })

  return (
    <section className="automationPayloadEditor" aria-label="Automation payload editor">
      <nav className="automationPayloadEditor__steps" aria-label="Automation editor steps">
        {STEPS.map((step, index) => (
          <button
            key={step.id}
            type="button"
            className={activeStep === step.id ? 'isActive' : ''}
            aria-current={activeStep === step.id ? 'step' : undefined}
            onClick={() => setActiveStep(step.id)}
          >
            <span>{index + 1}</span>
            {step.label}
          </button>
        ))}
      </nav>
      <div className="automationPayloadEditor__workspace">
        <div className="automationPayloadEditor__stage">
          {activeStep === 'trigger' ? (
            <PayloadList title="Trigger" action={draft.triggers.length < 2 ? (
              <Button type="button" variant="secondary" size="sm" icon={<AddRoundedIcon fontSize="inherit" />} disabled={disabled || draft.triggers.length >= 2} onClick={() => addTrigger(triggersForSearch[0]?.type ?? 'NODE_CREATED')}>Add trigger</Button>
            ) : null}>
              <label className="automationPayloadEditor__search">
                <span>Search triggers</span>
                <input value={triggerSearch} disabled={disabled} placeholder="AI_REFINE_USED, CHECKLIST_COMPLETED..." onChange={(event) => setTriggerSearch(event.target.value)} />
              </label>
              {draft.triggers.map((trigger, index) => (
                <PayloadRow key={`trigger-${index}`} dragLabel={`Drag trigger ${index + 1}`} deleteLabel={`Delete trigger ${index + 1}`} disabled={disabled} onDelete={() => emitDraft({ ...draft, triggers: draft.triggers.filter((_, itemIndex) => itemIndex !== index) })} onDragStart={() => { draggedTriggerRef.current = index }} onDrop={() => {
                  const from = draggedTriggerRef.current
                  draggedTriggerRef.current = null
                  if (from !== null) emitDraft({ ...draft, triggers: reorder(draft.triggers, from, index) })
                }}>
                  <select aria-label={`Trigger ${index + 1} type`} value={trigger.type} disabled={disabled} onChange={(event) => {
                    const type = event.target.value as AutomationTriggerType
                    updateTrigger(index, createDefaultTrigger(type))
                  }}>
                    {triggersForSearch.map((schema) => <option key={schema.type} value={schema.type}>{schema.type} - {schema.label}</option>)}
                  </select>
                  <ConfigFields fields={TRIGGER_SCHEMAS[trigger.type].fields} config={asRecord(trigger.config)} disabled={disabled} onChange={(config) => updateTrigger(index, { ...trigger, config } as AutomationTrigger)} />
                </PayloadRow>
              ))}
            </PayloadList>
          ) : activeStep === 'actions' ? (
            <PayloadList title="Actions" action={(
              <div className="automationPayloadEditor__addAction">
                <select aria-label="Action type to add" value={actionTypeToAdd} disabled={disabled} onChange={(event) => setActionTypeToAdd(event.target.value as AutomationActionType)}>
                  {Object.values(ACTION_SCHEMAS).map((schema) => <option key={schema.type} value={schema.type}>{schema.type}</option>)}
                </select>
                <Button type="button" variant="secondary" size="sm" icon={<AddRoundedIcon fontSize="inherit" />} disabled={disabled} onClick={addAction}>Add action</Button>
              </div>
            )}>
              {draft.then.map((action, index) => (
                <PayloadRow key={`${action.id}-${index}`} dragLabel={`Drag action ${index + 1}`} deleteLabel={`Delete action ${index + 1}`} disabled={disabled} onDelete={() => emitDraft({ ...draft, then: draft.then.filter((_, itemIndex) => itemIndex !== index) })} onDragStart={() => { draggedActionRef.current = index }} onDrop={() => {
                  const from = draggedActionRef.current
                  draggedActionRef.current = null
                  if (from !== null) emitDraft({ ...draft, then: reorder(draft.then, from, index) })
                }}>
                  <div className="automationPayloadEditor__actionTop">
                    <label><span>ID</span><input aria-label={`Action ${index + 1} id`} value={action.id} disabled={disabled} onChange={(event) => updateAction(index, { ...action, id: event.target.value })} /></label>
                    <label>
                      <span>Type</span>
                      <select aria-label={`Action ${index + 1} type`} value={action.type} disabled={disabled} onChange={(event) => {
                        const type = event.target.value as AutomationActionType
                        updateAction(index, { id: action.id, type, config: createDefaultActionConfig(type) } as AutomationAction)
                      }}>
                        {Object.values(ACTION_SCHEMAS).map((schema) => <option key={schema.type} value={schema.type}>{schema.type} - {schema.label}</option>)}
                      </select>
                    </label>
                  </div>
                  <p>{ACTION_SCHEMAS[action.type].description}</p>
                  <ConfigFields fields={ACTION_SCHEMAS[action.type].fields} config={asRecord(action.config)} disabled={disabled} onChange={(config) => updateAction(index, { ...action, config } as AutomationAction)} />
                </PayloadRow>
              ))}
            </PayloadList>
          ) : activeStep === 'guards' ? (
            <PayloadList title="Guards" action={(
              <Button type="button" variant="secondary" size="sm" icon={<AddRoundedIcon fontSize="inherit" />} disabled={disabled} onClick={() => emitDraft({ ...draft, if: { join: draft.if?.join ?? 'AND', clauses: [...conditions, { path: 'payload.value', op: 'EQ', value: true }] } })}>Add condition</Button>
            )}>
              <label className="automationPayloadEditor__constraint">
                <span>Custom constraint JSON</span>
                <textarea aria-label="Custom constraint JSON" value={JSON.stringify(draft.constraint ?? {}, null, 2)} disabled={disabled} onChange={(event) => {
                  const constraint = parseJsonObject(event.target.value)
                  emitDraft({ ...draft, constraint: Object.keys(constraint).length > 0 ? constraint : undefined })
                }} />
              </label>
              <label className="automationPayloadEditor__join">
                <span>IF join</span>
                <select aria-label="Condition join" value={draft.if?.join ?? 'AND'} disabled={disabled} onChange={(event) => emitDraft({ ...draft, if: { join: event.target.value as 'AND' | 'OR', clauses: conditions } })}>
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
              </label>
              {conditions.map((condition, index) => (
                <PayloadRow key={`condition-${index}`} dragLabel={`Drag condition ${index + 1}`} deleteLabel={`Delete condition ${index + 1}`} disabled={disabled} onDelete={() => emitDraft({ ...draft, if: withoutEmptyCondition({ join: draft.if?.join ?? 'AND', clauses: conditions.filter((_, itemIndex) => itemIndex !== index) }) })} onDragStart={() => { draggedConditionRef.current = index }} onDrop={() => {
                  const from = draggedConditionRef.current
                  draggedConditionRef.current = null
                  if (from !== null) emitDraft({ ...draft, if: { join: draft.if?.join ?? 'AND', clauses: reorder(conditions, from, index) } })
                }}>
                  <input aria-label={`Condition ${index + 1} path`} value={condition.path} disabled={disabled} onChange={(event) => updateCondition(index, { ...condition, path: event.target.value })} />
                  <select aria-label={`Condition ${index + 1} op`} value={condition.op} disabled={disabled} onChange={(event) => updateCondition(index, { ...condition, op: event.target.value as AutomationConditionOp })}>
                    {CONDITION_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
                  </select>
                  <input aria-label={`Condition ${index + 1} value`} value={condition.value === undefined ? '' : JSON.stringify(condition.value)} disabled={disabled} onChange={(event) => updateCondition(index, { ...condition, value: parseConditionValue(event.target.value) })} />
                </PayloadRow>
              ))}
            </PayloadList>
          ) : (
            <PayloadList title="Save" action={null}>
              <div className="automationPayloadEditor__summaryText">
                <strong>{validation.length === 0 ? 'Ready to save' : `${validation.length} item${validation.length === 1 ? '' : 's'} need attention`}</strong>
                <p>{plainSummary(draft)}</p>
                {validation.map((item) => <small key={item}>{item}</small>)}
              </div>
              <pre className="automationPayloadEditor__jsonBlock">{JSON.stringify(compiledPayload, null, 2)}</pre>
            </PayloadList>
          )}
        </div>
        <aside className="automationPayloadEditor__rail" aria-label="Current workflow">
          <span>Current workflow</span>
          <strong>{draft.name}</strong>
          <pre>{JSON.stringify(compiledPayload, null, 2)}</pre>
        </aside>
      </div>
    </section>
  )
}

const plainSummary = (draft: GraphDraft) => {
  const triggers = draft.triggers.map((trigger) => TRIGGER_SCHEMAS[trigger.type].label).join(' or ') || 'No trigger'
  const actions = draft.then.map((action) => ACTION_SCHEMAS[action.type].label.toLowerCase()).join(', ') || 'no actions'
  const guard = draft.if?.clauses.length ? ` Guarded by ${draft.if.clauses.length} IF condition${draft.if.clauses.length === 1 ? '' : 's'}.` : ''
  return `When ${triggers}, run ${actions}.${guard}`
}

const schemaKeys = (fields: RegistryField[]) => new Set(fields.map((field) => field.key))

const ConfigFields = ({
  fields,
  config,
  disabled,
  onChange,
}: {
  fields: RegistryField[]
  config: Record<string, unknown>
  disabled: boolean
  onChange: (next: Record<string, unknown>) => void
}) => {
  const known = schemaKeys(fields)
  const extras = Object.fromEntries(Object.entries(config).filter(([key]) => !known.has(key)))
  const update = (key: string, fieldValue: unknown) => onChange({ ...config, [key]: fieldValue })
  const updateExtras = (nextExtras: Record<string, unknown>) => {
    const next = Object.fromEntries(Object.entries(config).filter(([key]) => known.has(key)))
    onChange({ ...next, ...nextExtras })
  }

  return (
    <div className="automationPayloadEditor__config">
      {fields.map((field) => (
        <SchemaField key={field.key} field={field} value={config[field.key] ?? ''} disabled={disabled} onChange={(next) => update(field.key, next)} />
      ))}
      {Object.keys(extras).length > 0 ? (
        <label className="automationPayloadEditor__extras">
          <span>Additional config JSON</span>
          <textarea aria-label="Additional config JSON" value={JSON.stringify(extras, null, 2)} disabled={disabled} onChange={(event) => updateExtras(parseJsonObject(event.target.value))} />
        </label>
      ) : null}
    </div>
  )
}

const SchemaField = ({
  field,
  value,
  disabled,
  onChange,
}: {
  field: RegistryField
  value: unknown
  disabled: boolean
  onChange: (next: unknown) => void
}) => {
  if (field.kind === 'NUMBER') {
    return (
      <label>
        <span>{field.label}</span>
        <NumberStepper
          ariaLabel={field.label}
          value={Number(value) || 0}
          min={0}
          max={field.key === 'amount' ? 1000000 : 86400}
          step={field.key === 'amount' ? 10 : 1}
          variant="orbit"
          size="sm"
          disabled={disabled}
          onChange={onChange}
        />
      </label>
    )
  }
  if (field.kind === 'SELECT') {
    return (
      <label>
        <span>{field.label}</span>
        <select aria-label={field.label} value={typeof value === 'string' ? value : ''} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
          <option value="">Select...</option>
          {(field.options ?? []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </label>
    )
  }
  if (field.kind === 'TOGGLE') {
    return (
      <label className="automationPayloadEditor__toggle">
        <span>{field.label}</span>
        <input aria-label={field.label} type="checkbox" checked={value === true} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      </label>
    )
  }
  if (field.kind === 'TEXTAREA') {
    return (
      <label>
        <span>{field.label}</span>
        <textarea aria-label={field.label} value={typeof value === 'string' ? value : ''} placeholder={field.placeholder} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      </label>
    )
  }
  return (
    <label>
      <span>{field.label}</span>
      <input aria-label={field.label} value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''} placeholder={field.placeholder} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    </label>
  )
}

const PayloadList = ({ title, action, children }: { title: string; action: ReactNode; children: ReactNode }) => (
  <section className="automationPayloadEditor__list">
    <header>
      <strong>{title}</strong>
      {action}
    </header>
    {children}
  </section>
)

const PayloadRow = ({
  dragLabel,
  deleteLabel,
  disabled,
  onDragStart,
  onDrop,
  onDelete,
  children,
}: {
  dragLabel: string
  deleteLabel: string
  disabled: boolean
  onDragStart: () => void
  onDrop: () => void
  onDelete: () => void
  children: ReactNode
}) => (
  <div className="automationPayloadEditor__row" onDragOver={(event) => event.preventDefault()} onDrop={onDrop}>
    <button type="button" className="checklistPayloadEditor__drag" draggable={!disabled} disabled={disabled} aria-label={dragLabel} onDragStart={onDragStart}><DragIndicatorRoundedIcon fontSize="inherit" /></button>
    <div className="automationPayloadEditor__rowFields">{children}</div>
    <button type="button" className="checklistPayloadEditor__iconButton" disabled={disabled} aria-label={deleteLabel} onClick={onDelete}><DeleteRoundedIcon fontSize="inherit" /></button>
  </div>
)
